import React, { useEffect, useRef } from "react";
import { useAdaptive } from "../lib/adaptive";

// ============================================================================
// AmbientContinuum — a single fixed-position canvas that lives behind every
// section. It morphs colour palette + particle density + motion vector based
// on the currently active section and scroll velocity. Result: distinct
// sections feel like one continuous cinematic environment.
// ============================================================================

// Per-section atmosphere descriptor
const ATMOSPHERES = {
    hero:        { hue: 38,  sat: 100, density: 0.7, drift: { x: 0.4, y: -0.1 }, mood: "void" },
    evolution:   { hue: 38,  sat: 100, density: 0.5, drift: { x: 0.8, y: 0.0  }, mood: "drift" },
    reveal:      { hue: 38,  sat: 100, density: 1.0, drift: { x: 0.0, y: 0.0  }, mood: "core" },
    backbone:    { hue: 38,  sat: 100, density: 0.9, drift: { x: 0.1, y: 0.0  }, mood: "orbit" },
    fabric:      { hue: 38,  sat: 100, density: 1.2, drift: { x: 0.6, y: 0.1  }, mood: "swarm" },
    simulator:   { hue: 174, sat: 60,  density: 0.6, drift: { x: 0.0, y: 0.2  }, mood: "thought" },
    products:    { hue: 38,  sat: 100, density: 0.5, drift: { x: -0.2, y: 0.0 }, mood: "lattice" },
    developers:  { hue: 174, sat: 50,  density: 0.7, drift: { x: 0.3, y: 0.1  }, mood: "code" },
    sandbox:     { hue: 50,  sat: 90,  density: 0.6, drift: { x: 0.2, y: 0.1  }, mood: "wire" },
    command:     { hue: 38,  sat: 100, density: 1.1, drift: { x: 0.5, y: 0.0  }, mood: "telemetry" },
    singularity: { hue: 38,  sat: 100, density: 1.4, drift: { x: 0.0, y: -0.05}, mood: "apex" },
    connect:     { hue: 38,  sat: 100, density: 0.4, drift: { x: 0.0, y: 0.0  }, mood: "still" },
};

const DEFAULT_ATMOSPHERE = ATMOSPHERES.hero;
const PARTICLE_COUNT = 130;

export default function AmbientContinuum() {
    const cvsRef = useRef(null);
    const { activeSection, scrollVelocity } = useAdaptive();
    const activeRef = useRef(activeSection);
    const velRef = useRef(scrollVelocity);
    activeRef.current = activeSection;
    velRef.current = scrollVelocity;

    useEffect(() => {
        const cvs = cvsRef.current;
        if (!cvs) return;
        const ctx = cvs.getContext("2d", { willReadFrequently: true });
        const dpr = Math.min(window.devicePixelRatio || 1, 2);

        const resize = () => {
            cvs.width = Math.floor(window.innerWidth * dpr);
            cvs.height = Math.floor(window.innerHeight * dpr);
            ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        };
        resize();
        window.addEventListener("resize", resize);

        // Smoothed atmosphere — eases between targets, no hard cuts
        const current = { hue: 38, sat: 100, density: 0.7, dx: 0.4, dy: -0.1 };

        // Particles share one pool that lives across the entire page
        const particles = Array.from({ length: PARTICLE_COUNT }).map(() => ({
            x: Math.random() * window.innerWidth,
            y: Math.random() * window.innerHeight,
            vx: (Math.random() - 0.5) * 0.4,
            vy: (Math.random() - 0.5) * 0.4,
            life: Math.random(),
            sz: 0.6 + Math.random() * 1.6,
            phase: Math.random() * Math.PI * 2,
        }));

        let raf = 0;
        let lastTs = 0;
        const draw = (ts) => {
            const dt = lastTs ? Math.min(0.05, (ts - lastTs) / 1000) : 0.016;
            lastTs = ts;

            const target = ATMOSPHERES[activeRef.current] || DEFAULT_ATMOSPHERE;
            // ease toward target atmosphere
            current.hue     += (target.hue - current.hue) * 0.025;
            current.sat     += (target.sat - current.sat) * 0.025;
            current.density += (target.density - current.density) * 0.025;
            current.dx      += (target.drift.x - current.dx) * 0.04;
            current.dy      += (target.drift.y - current.dy) * 0.04;

            const w = window.innerWidth, h = window.innerHeight;
            const velBoost = Math.min(3, 1 + velRef.current * 6);

            // wipe with very low alpha for motion trail
            ctx.fillStyle = "rgba(5,4,3,0.18)";
            ctx.fillRect(0, 0, w, h);

            const activeCount = Math.min(
                particles.length,
                Math.floor(PARTICLE_COUNT * current.density)
            );
            for (let i = 0; i < activeCount; i++) {
                const p = particles[i];
                if (!p) continue;
                p.life += dt * 0.4;
                if (p.life > 1) p.life = 0;
                p.x += (p.vx + current.dx * 0.6) * velBoost;
                p.y += (p.vy + current.dy * 0.6) * velBoost;
                // wrap
                if (p.x < -10) p.x = w + 10;
                if (p.x > w + 10) p.x = -10;
                if (p.y < -10) p.y = h + 10;
                if (p.y > h + 10) p.y = -10;
                p.phase += dt * 1.6;

                const alpha = (0.25 + Math.sin(p.phase) * 0.35) * (0.6 + current.density * 0.3);
                ctx.fillStyle = `hsla(${current.hue}, ${current.sat}%, 56%, ${Math.max(0, alpha)})`;
                ctx.beginPath();
                ctx.arc(p.x, p.y, p.sz * (0.8 + Math.sin(p.phase) * 0.3), 0, Math.PI * 2);
                ctx.fill();

                // halo for the brightest 30%
                if (i % 4 === 0) {
                    ctx.fillStyle = `hsla(${current.hue}, ${current.sat}%, 56%, ${alpha * 0.18})`;
                    ctx.beginPath();
                    ctx.arc(p.x, p.y, p.sz * 4, 0, Math.PI * 2);
                    ctx.fill();
                }
            }

            // Subtle connecting lines between nearby particles — only when scrolling slowly
            if (velRef.current < 0.3) {
                ctx.strokeStyle = `hsla(${current.hue}, ${current.sat}%, 56%, 0.06)`;
                ctx.lineWidth = 0.4;
                for (let i = 0; i < activeCount; i += 2) {
                    const p = particles[i];
                    const q = particles[(i + 7) % activeCount];
                    if (!p || !q) continue;
                    const dx = p.x - q.x, dy = p.y - q.y;
                    const d = dx * dx + dy * dy;
                    if (d < 12000) {
                        ctx.beginPath();
                        ctx.moveTo(p.x, p.y); ctx.lineTo(q.x, q.y);
                        ctx.stroke();
                    }
                }
            }

            raf = requestAnimationFrame(draw);
        };
        raf = requestAnimationFrame(draw);
        return () => { cancelAnimationFrame(raf); window.removeEventListener("resize", resize); };
    }, []);

    return (
        <canvas
            ref={cvsRef}
            aria-hidden="true"
            className="pointer-events-none fixed inset-0 w-screen h-screen z-[20]"
            style={{ mixBlendMode: "screen", opacity: 0.45 }}
        />
    );
}
